"use client";

// React and hooks
import { useContext, useState, useEffect } from "react";
import { useNotification } from "../contexts/NotificationContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import RegistrationForm from "../components/RegistrationForm";
import ForgotPasswordForm from "../components/ForgotPasswordForm";
import FloatingElements3D from "../components/FloatingElements3D";
import Logo3D from "../components/Logo3D";

// Context
import { LoginContext } from "../LoginContext";

// Services
import { apiService } from "../backend/api";

// Icons
import {
  Eye,
  EyeOff,
  LogIn,
  UserPlus,
  Lock,
  User,
  AlertCircle
} from "lucide-react";

const PageLogin = () => {
  const { showNotification } = useNotification();
  const { login } = useContext(LoginContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);

  // Open registration modal if ?register=true is in URL
  useEffect(() => {
    if (searchParams.get("register") === "true") {
      setIsRegisterOpen(true);
    }
  }, [searchParams]);
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPw, setShowForgotPw] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleLogin = async () => {
    if (!username || !password) {
      showNotification(t("Please fill in all fields"), "warning");
      return;
    }

    setIsLoading(true);
    setLocalError(null);
    try {
      const data = await apiService.loginUser(username, password);
      login(data.token, username, data.user);
      navigate("/");

      try {
        await apiService.getUserRoles(username);
      } catch (err) {
        console.error("Error fetching roles:", err);
      }
    } catch (error) {
      setLocalError(t("loginError") + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div className="min-h-[calc(100dvh-5rem)] bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] dark:from-slate-900 dark:to-slate-800 flex items-center justify-center relative overflow-hidden py-4 sm:py-8 transition-colors duration-300">
      {/* Floating 3D Background Elements */}
      <FloatingElements3D count={6} />

      <div className="max-w-xl w-full px-4 relative z-[1]">
        {/* Login Card */}
        <div className="animate-in zoom-in duration-800">
          <div className="max-w-[500px] mx-auto p-6 sm:p-12 rounded-[2rem] bg-white/95 dark:bg-slate-800/95 backdrop-blur-[20px] border border-white/30 dark:border-slate-700/30 shadow-[0_20px_60px_rgba(15,118,153,0.15),0_8px_24px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_60px_rgba(0,0,0,0.3),0_8px_24px_rgba(0,0,0,0.2)] transition-all duration-400 ease-in-out hover:-translate-y-1 hover:shadow-[0_28px_80px_rgba(15,118,153,0.2),0_12px_32px_rgba(0,0,0,0.15)] dark:hover:shadow-[0_28px_80px_rgba(0,0,0,0.4),0_12px_32px_rgba(0,0,0,0.25)] preserve-3d relative">

            {/* Logo and Title */}
            <div className="text-center mb-6 sm:mb-10">
              <div className="flex justify-center mb-3 sm:mb-5">
                <div className="sm:hidden"><Logo3D size={44} /></div>
                <div className="hidden sm:block"><Logo3D size={60} /></div>
              </div>

              <h1 className="text-2xl sm:text-4xl font-[800] bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] dark:from-[#4fc3f7] dark:to-[#81d4fa] bg-clip-text text-transparent mb-1 sm:mb-2">
                {t("Login")}
              </h1>

              <p className="hidden sm:block text-slate-500 dark:text-slate-400 text-[0.95rem]">
                Welcome back to 3D-Medico
              </p>
            </div>

            {/* Error Message Section */}
            {localError && (
              <div className="mb-6 p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 shrink-0" />
                <div className="text-sm font-semibold text-red-700 dark:text-red-300 leading-tight">
                  {localError}
                </div>
              </div>
            )}

            {/* Username Field */}
            <div className="relative mb-4 sm:mb-6 group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <div className="w-8 h-8 rounded-full bg-[#0f7699] dark:bg-[#4fc3f7] flex items-center justify-center text-white dark:text-slate-900 shadow-lg shadow-[#0f7699]/20 dark:shadow-[#4fc3f7]/20">
                  <User size={16} />
                </div>
              </div>
              <input
                type="text"
                placeholder={t("username")}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full h-12 sm:h-14 pl-16 pr-6 bg-slate-50/80 dark:bg-slate-700/80 border-2 border-transparent focus:border-[#0f7699] dark:focus:border-[#4fc3f7] focus:bg-white dark:focus:bg-slate-700 rounded-2xl text-slate-700 dark:text-slate-200 font-bold placeholder:text-slate-400 dark:placeholder:text-slate-500 transition-all outline-none hover:bg-slate-50 dark:hover:bg-slate-700 focus:shadow-[0_4px_12px_rgba(15,118,153,0.15)] dark:focus:shadow-[0_4px_12px_rgba(79,195,247,0.15)]"
              />
            </div>

            {/* Password Field */}
            <div className="relative mb-3 sm:mb-4 group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <div className="w-8 h-8 rounded-full bg-[#0f7699] dark:bg-[#4fc3f7] flex items-center justify-center text-white dark:text-slate-900 shadow-lg shadow-[#0f7699]/20 dark:shadow-[#4fc3f7]/20">
                  <Lock size={16} />
                </div>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t("password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full h-12 sm:h-14 pl-16 pr-14 bg-slate-50/80 dark:bg-slate-700/80 border-2 border-transparent focus:border-[#0f7699] dark:focus:border-[#4fc3f7] focus:bg-white dark:focus:bg-slate-700 rounded-2xl text-slate-700 dark:text-slate-200 font-bold placeholder:text-slate-400 dark:placeholder:text-slate-500 transition-all outline-none hover:bg-slate-50 dark:hover:bg-slate-700 focus:shadow-[0_4px_12px_rgba(15,118,153,0.15)] dark:focus:shadow-[0_4px_12px_rgba(79,195,247,0.15)]"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-[#0f7699] dark:text-[#4fc3f7] hover:scale-110 active:scale-95 transition-all"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right mb-5 sm:mb-8">
              <button
                type="button"
                onClick={() => setShowForgotPw(true)}
                className="text-[#0f7699] dark:text-[#4fc3f7] text-[0.9rem] font-bold transition-all hover:underline hover:text-[#0a5f7a] dark:hover:text-[#81d4fa]"
              >
                {t("forgotPassword.title")}
              </button>
            </div>

            {/* Login Button */}
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full h-12 sm:h-14 rounded-2xl text-[1.05rem] font-black mb-3 sm:mb-4 bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] dark:from-[#4fc3f7] dark:to-[#81d4fa] text-white dark:text-slate-900 shadow-[0_8px_24px_rgba(15,118,153,0.3)] dark:shadow-[0_8px_24px_rgba(79,195,247,0.3)] transition-all duration-300 hover:from-[#0a5f7a] hover:to-[#0f7699] dark:hover:from-[#81d4fa] dark:hover:to-[#4fc3f7] hover:shadow-[0_12px_32px_rgba(15,118,153,0.4)] dark:hover:shadow-[0_12px_32px_rgba(79,195,247,0.4)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 flex items-center justify-center gap-2 group"
            >
              {isLoading ? (
                <div className="w-6 h-6 border-2 border-white/30 dark:border-slate-900/30 border-t-white dark:border-t-slate-900 rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn size={20} className="group-hover:translate-x-1 transition-transform" />
                  <span>{t("loginButton")}</span>
                </>
              )}
            </button>

            {/* Register Button */}
            <button
              onClick={() => setIsRegisterOpen(true)}
              className="w-full h-12 sm:h-14 rounded-2xl text-[1.05rem] font-black border-2 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7] transition-all duration-300 hover:bg-[#0f7699]/5 dark:hover:bg-[#4fc3f7]/10 hover:border-[#0a5f7a] dark:hover:border-[#81d4fa] hover:text-[#0a5f7a] dark:hover:text-[#81d4fa] hover:shadow-[0_8px_24px_rgba(15,118,153,0.2)] dark:hover:shadow-[0_8px_24px_rgba(79,195,247,0.2)] hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              <UserPlus size={20} />
              <span>{t("registerButton")}</span>
            </button>

            {/* Decorative Element */}
            <div className="absolute -top-5 -right-5 w-24 h-24 rounded-full bg-radial-gradient from-[#0f7699]/10 dark:from-[#4fc3f7]/10 to-transparent pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Modals */}
      {isRegisterOpen && (
        <RegistrationForm onClose={() => setIsRegisterOpen(false)} />
      )}

      {showForgotPw && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[2000] p-4 animate-in fade-in duration-300">
          <ForgotPasswordForm onClose={() => setShowForgotPw(false)} />
        </div>
      )}
    </div>
  );
};

export default PageLogin;
